// src/components/ChartSection.tsx
import React, { useState, useEffect } from 'react';
import { Button, ButtonGroup, Card } from 'react-bootstrap';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import * as echarts from 'echarts';

type SeriesItem = {
  data: (number | null)[];
  label: string;
  // color?: string;  <-- eliminamos esta propiedad para no depender de ella
};

type ChartSectionProps = {
  dailyLabels: string[];
  dailySeries: SeriesItem[];

  monthlyLabels: string[];
  monthlySeries: SeriesItem[];
};

interface AxisData {
  date: Date;
  label: string;
}

const ChartSection: React.FC<ChartSectionProps> = ({
  dailyLabels,
  dailySeries,
  monthlyLabels,
  monthlySeries,
}) => {
  const [timeRange, setTimeRange] = useState<'1w' | '1m' | '6m' | '1y'>('1w');
  const [xAxisData, setXAxisData] = useState<AxisData[]>([]);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);


  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1150);
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Inicializar el estado

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Obtenemos el color de texto de la variable CSS --text-color para que ECharts se adapte al tema
  const getCssVar = (name: string) =>
    getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  const [textColor, setTextColor] = useState(getCssVar('--text-color'));

  useEffect(() => {
    const update = () => setTextColor(getCssVar('--text-color'));
    const obs = new MutationObserver(update);
    obs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-color-mode'],
    });
    update();
    return () => obs.disconnect();
  }, []);

  // Construimos las etiquetas del eje X según el rango elegido
  useEffect(() => {
    const now = new Date();
    const arr: AxisData[] = [];

    switch (timeRange) {
      case '1w': {
        for (let i = 6; i >= 0; i--) {
          const d = new Date(now);
          d.setDate(now.getDate() - i);
          arr.push({
            date: d,
            label: d.toLocaleDateString('es-ES', {
              day: 'numeric',
              month: 'short',
              year: 'numeric',
            }),
          });
        }
        break;
      }
      case '1m': {
        for (let i = 29; i >= 0; i--) {
          const d = new Date(now);
          d.setDate(now.getDate() - i);
          arr.push({
            date: d,
            label: d.toLocaleDateString('es-ES', {
              day: 'numeric',
              month: 'short',
              year: 'numeric',
            }),
          });
        }
        break;
      }
      case '6m': {
        for (let i = 5; i >= 0; i--) {
          const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
          arr.push({
            date: d,
            label: d.toLocaleDateString('es-ES', {
              month: 'short',
              year: 'numeric',
            }),
          });
        }
        break;
      }
      case '1y': {
        for (let i = 11; i >= 0; i--) {
          const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
          arr.push({
            date: d,
            label: d.toLocaleDateString('es-ES', {
              month: 'short',
              year: 'numeric',
            }),
          });
        }
        break;
      }
      default:
        break;
    }

    setXAxisData(arr);
  }, [timeRange]);

  const isDaily = timeRange === '1w' || timeRange === '1m';
  const labelsToUse = isDaily ? dailyLabels : monthlyLabels;
  const seriesToUse = isDaily ? dailySeries : monthlySeries;

  // Toma, para cada serie, un array con el valor correspondiente a cada fecha del eje X
  const buildSeriesValues = (s: SeriesItem): (number | null)[] => {
    return xAxisData.map(({ date }) => {
      if (isDaily) {
        const isoTarget = date.toISOString().split('T')[0];
        const idx = labelsToUse.findIndex((lbl) => {
          if (/^\d{4}-\d{2}-\d{2}$/.test(lbl)) {
            return lbl === isoTarget;
          }
          const parsed = new Date(lbl);
          return (
            !isNaN(parsed.getTime()) &&
            parsed.toISOString().split('T')[0] === isoTarget
          );
        });
        return idx >= 0 ? s.data[idx] : null;
      } else {
        const mes = date.getMonth();
        const anio = date.getFullYear();
        const idx = labelsToUse.findIndex((lbl) => {
          const partes = lbl.split(' ');
          if (partes.length === 2) {
            const [mesStr, anioStr] = partes;
            const mesesMap: Record<string, number> = {
              Enero: 0,
              Febrero: 1,
              Marzo: 2,
              Abril: 3,
              Mayo: 4,
              Junio: 5,
              Julio: 6,
              Agosto: 7,
              Septiembre: 8,
              Octubre: 9,
              Noviembre: 10,
              Diciembre: 11,
            };
            const clave =
              mesStr.charAt(0).toUpperCase() +
              mesStr.slice(1).toLowerCase();
            return (
              mesesMap[clave] === mes &&
              parseInt(anioStr, 10) === anio
            );
          }
          return false;
        });
        return idx >= 0 ? s.data[idx] : null;
      }
    });
  };

  // ─── Palette fija con los cuatro colores solicitados ────────────────────
const baseColors = [
  // Colores originales
  '#FC7556', 
  '#E85B44', 
  '#FFB388', 
  '#6b6b6b', 
  '#FCA78D', // más claro que #FC7556
  '#E2382E', // más oscuro que #E85B44
  '#FFE1B3', // más claro que #FFB388
  '#CC6A56', // intermedio/ligeramente más oscuro que #FC7556
  '#9a9a9a', // variante clara de #6b6b6b
  '#404040', // variante oscura de #6b6b6b
];


  // Construimos la configuración de cada serie (bar) usando únicamente esos colores
  // y asignando uno a cada índice idx = 0,1,2,3
  const seriesOptions: echarts.BarSeriesOption[] = seriesToUse.map(
    (s, idx) => {
      const values = buildSeriesValues(s);
      // Si prefieres ciclos, podrías hacer idx % baseColors.length, pero aquí suponemos
      // que tienes 4 series en orden: Brutas, Netas, Propinas, Pérdidas.
      const baseColor = baseColors[idx];

      return {
        name: s.label,
        type: 'bar',
        barGap: 0,
        barCategoryGap: '20%',
        label: { show: false },
        emphasis: { focus: 'series' },
        itemStyle: {
          borderRadius: [6, 6, 0, 0],
          // Aquí asignamos directamente el color plano (sin degradado)
          color: baseColor,
        },
        data: values,
      };
    }
  );

  const option: EChartsOption = {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: 'rgba(50,50,50,0.7)',
      textStyle: { color: '#fff' },
      borderRadius: 4,
      padding: [8, 12],
    },
    legend: {
      data: seriesToUse.map((s) => s.label),
      textStyle: { color: textColor },
      top: 10,
      itemGap: 20,
      icon: 'roundRect',
      type: 'scroll',
      selectedMode: true,
    },
    toolbox: {
      feature: { saveAsImage: {} },
      right: 20,
    },
    grid: {
      left: isMobile ? '9%' : isTablet ? '5%' : '2%',
      right: '2%',
      bottom: '7%',
      top: '15%',
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      name: 'Fecha',
      nameLocation: 'middle',
      nameGap: 75,
      data: xAxisData.map((item) => item.label),
      axisTick: { show: false },
      axisLine: { lineStyle: { color: textColor } },
      axisLabel: {
        color: textColor,
        rotate: 45,
        formatter: (value: string) => value,
        lineHeight: 20,
      },
    },
    yAxis: {
      name: 'Cantidad',
      nameLocation: 'middle',
      nameGap: 52,
      type: 'value',
      min: 0,
      axisLine: { lineStyle: { color: textColor } },
      axisLabel: { color: textColor },
      splitLine: {
        show: true,
        lineStyle: { color: '#eee', type: 'dashed' },
      },
    },
    series: seriesOptions,
  };

  return (
    <Card className="card-Barras-Bills">
      <Card.Body style={{ padding: '1rem 1.5rem' }}>
        {/* Botones de selección de rango de tiempo */}
        <ButtonGroup className="mb-3">
          {[
            { key: '1w', label: '1S' },
            { key: '1m', label: '1M' },
            { key: '6m', label: '6M' },
            { key: '1y', label: '1A' },
          ].map((btn) => (
            <Button
              key={btn.key}
              size="sm"
              variant="none"
              className={`time-range-button time-range-button-1w`}
              onClick={() => setTimeRange(btn.key as any)}
            >
              {btn.label}
            </Button>
          ))}
        </ButtonGroup>

        {/* Gráfico de barras agrupadas */}
        <ReactECharts option={option} style={{ height: 450, width: '100%' }} />
      </Card.Body>
    </Card>
  );
};

export default ChartSection;
